import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FUNCTIONS_URL, supabase } from '../lib/supabase';
import { authCallbackUrl } from '../lib/urls';
import { clearPendingPurchase, readPendingPurchase } from '../lib/pending-purchase';

// Post-LS handoff. Three phases visible to the user:
//
//   working — polling post-purchase-signin for up to ~45s. Silent to the
//             user for the first few seconds (usually resolves fast).
//   slow    — >= SLOW_SECONDS and still pending. We acknowledge the wait
//             and surface a manual "email me a sign-in link" escape.
//   manual  — >= MAX_SECONDS, hard server error, or verifyOtp failed.
//             We stop polling and make the escape the primary action.
//
// The purchase is already committed in LS before we even got here, so any
// failure on our side is a sign-in failure, not a payment failure — the
// copy has to make that distinction.

type Phase = 'working' | 'slow' | 'manual';

const SLOW_SECONDS = 6;
const MAX_SECONDS = 45;
const POLL_INTERVAL_MS = 1500;

export function Success() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const email = params.get('email') ?? readPendingPurchase()?.email ?? null;
  const orderId = params.get('order_id');
  const nonce = params.get('nonce') ?? readPendingPurchase()?.nonce ?? null;

  const [phase, setPhase] = useState<Phase>('working');
  const [manualSent, setManualSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [manualError, setManualError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    if (!nonce) {
      setPhase('manual');
      return;
    }

    const start = Date.now();
    let cancelled = false;
    const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const elapsed = () => Math.floor((Date.now() - start) / 1000);

    const poll = async () => {
      while (!cancelled) {
        if (elapsed() >= MAX_SECONDS) {
          setPhase('manual');
          return;
        }
        if (elapsed() >= SLOW_SECONDS) {
          setPhase((p) => (p === 'working' ? 'slow' : p));
        }

        try {
          const res = await fetch(`${FUNCTIONS_URL}/post-purchase-signin`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              apikey: anon,
              Authorization: `Bearer ${anon}`,
            },
            body: JSON.stringify({ nonce }),
          });

          if (res.status === 202) {
            await sleep(POLL_INTERVAL_MS);
            continue;
          }

          if (!res.ok) {
            setPhase('manual');
            return;
          }

          const { token_hash } = (await res.json()) as { token_hash: string };
          const { error: verifyErr } = await supabase.auth.verifyOtp({
            token_hash,
            type: 'magiclink',
          });
          if (verifyErr) {
            setPhase('manual');
            return;
          }
          if (cancelled) return;
          clearPendingPurchase();
          navigate('/manage', { replace: true });
          return;
        } catch {
          // network blip — backoff and retry; the phase timers above still
          // tick us into slow/manual if it persists.
          await sleep(POLL_INTERVAL_MS * 2);
        }
      }
    };

    void poll();
    return () => { cancelled = true; };
  }, [navigate, nonce]);

  const sendManualLink = async () => {
    if (!email) return;
    setManualError(null);
    setSending(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: authCallbackUrl() },
      });
      if (error) throw error;
      setManualSent(true);
    } catch (err) {
      setManualError(err instanceof Error ? err.message : 'Failed to send link.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <h1>Thanks for your purchase.</h1>
      {email && (
        <p className="muted">
          {phase === 'manual'
            ? `Your calendar's ready. Let's sign you in as ${email}.`
            : `Signing you in as ${email}.`}
        </p>
      )}

      {phase === 'working' && <p className="soft">Just a moment…</p>}
      {phase === 'slow' && (
        <p className="soft">This usually takes a few seconds. Almost there.</p>
      )}
      {phase === 'manual' && !manualSent && (
        <p className="soft">
          We couldn't sign you in automatically on this browser — but your
          purchase is safe. Send a magic link to your inbox:
        </p>
      )}

      {(phase === 'slow' || phase === 'manual') && email && !manualSent && (
        <button className="primary" onClick={sendManualLink} disabled={sending}>
          {sending ? 'Sending…' : 'Email me a sign-in link'}
        </button>
      )}

      {manualSent && <p className="notice">Sent. Check {email}.</p>}
      {manualError && <p className="error">{manualError}</p>}

      {phase === 'manual' && !email && (
        <p className="muted">
          Check your email for a sign-in link from Outside Calendar — we send one
          automatically when a purchase completes.
        </p>
      )}

      {orderId && <p className="soft" style={{ marginTop: '2rem' }}>Order {orderId}</p>}
    </>
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
